import useClientServices from 'hooks/useClientServices';
import useIsMobile from 'hooks/useIsMobile';
import useSpaceSearch, {
  getSearchData,
  SpaceSearchData,
} from 'hooks/useSpaceSearch';
import useStoredBooking, { getInitialValues } from 'hooks/useStoredBooking';
import {
  ComponentProps,
  FC,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { AuthContext } from 'state/auth';
import { SearchContext } from 'state/search';
import type { Venue as VenueModel } from 'types/domain';
import { spaceDisabledForService } from 'utils/booking';
import { dateFormatForAPI, formatDate } from 'utils/date';
import { useTranslation } from 'utils/i18n';
import { scrollIntoViewWithOffset } from 'utils/navigation';

import FloorplansButton from '../floorplansButton';

import SpaceFilters from '../spaceFilters';
import SpaceListBase from '../spaceList';
import VenueCalendar from '../venueCalendar';

type SearchSpace = NonNullable<
  SpaceSearchData['venues'][number]['spaces']
>[number];

// The space/venue shapes SpaceList's own onAddToBooking declares.
type ListSpace = Parameters<
  NonNullable<ComponentProps<typeof SpaceListBase>['onAddToBooking']>
>[0];
type ListVenue = Parameters<
  NonNullable<ComponentProps<typeof SpaceListBase>['onAddToBooking']>
>[1];

// The search response's space/venue rows are structurally compatible with what
// SpaceList renders but not declared identically; relax only those two props.
const SpaceList = SpaceListBase as unknown as FC<
  Omit<
    ComponentProps<typeof SpaceListBase>,
    'spaces' | 'venue' | 'selectedSpaceIds'
  > & {
    spaces?: SearchSpace[];
    venue?: Venue;
    selectedSpaceIds?: number[];
  }
>;

type View = ComponentProps<typeof SpaceFilters>['currentViewType'];

type Venue = ComponentProps<typeof SpaceListBase>['venue'] &
  ComponentProps<typeof VenueCalendar>['venue'] &
  Pick<VenueModel, 'floorplans'>;

interface Props {
  venue: Venue;
  initialData?: SpaceSearchData;
  scrollIntoView: boolean;
  selectedVenueId?: number;
  selectedSpaceIds?: number[];
  // Called as (space, venue): SpaceList passes the space first; the old
  // parameter names here were swapped, though every caller already relied on
  // the real order.
  onAddToBooking?: (
    space: ListSpace,
    venue: ListVenue,
    options?: { date?: string }
  ) => void;
}

const VenueSpaces: FC<Props> = ({
  venue = {} as Venue,
  initialData = getSearchData([venue] as unknown as VenueModel[]),
  scrollIntoView = false,
  ...props
}) => {
  const { t } = useTranslation('common');
  const {
    filters: { date, ...filters },
    actions: { updateDate },
  } = useContext(SearchContext);

  const [{ service: serviceId } = {}] = useStoredBooking();
  const { data: services } = useClientServices();
  const service = useMemo(
    () =>
      serviceId
        ? services.find(({ id }) => id === Number(serviceId))
        : undefined,
    [services, serviceId]
  );

  const { loggedIn, accountApproved } = useContext(AuthContext);
  const isMobile = useIsMobile();

  const [view, changeViewType] = useReducer(
    (view: View): View => (view === 'calendar' ? 'list' : 'calendar'),
    'list' as View
  );

  const showCalendarToggle = !isMobile && loggedIn && accountApproved;
  const currentView = showCalendarToggle ? view : 'list';

  const dateSet = !!date;

  useEffect(() => {
    if (currentView === 'calendar' && !dateSet) updateDate?.(new Date());
  }, [dateSet, updateDate, currentView]);

  useEffect(() => {
    if (scrollIntoView) {
      window.scrollTo(0, 1800);
    }
  }, [scrollIntoView]);

  const { distance, capacity, facets } = filters;

  const {
    data: { venues: [{ spaces = [] } = {}] = [], aggregates } = initialData,
  } = useSpaceSearch({
    viewType: currentView,
    filters: {
      distance,
      capacity,
      facets,
      ...(currentView === 'calendar' ? {} : { date }),
      selectedVenueIds: venue.id ? [venue.id] : [],
    },
  });

  useEffect(() => {
    if (scrollIntoView && spaces.length > 0) {
      setTimeout(() => {
        scrollIntoViewWithOffset('#venue-spaces', 100);
      }, 200);
    }
  }, [scrollIntoView, spaces]);

  const renderList = () => {
    return spaces.length > 0 ? (
      <SpaceList
        {...props}
        spaces={spaces}
        spaceServiceClash={spaceDisabledForService(service)}
        venue={venue}
        onAddToBooking={(space, venue) => {
          props.onAddToBooking?.(space, venue, { date: date ?? undefined });
        }}
        fetchSpaces={false}
      />
    ) : (
      <div className="venue-search__alternative_venues_message">
        <p>
          {t(
            'search_results.alternative_venues.no_matches_with_capacity_filter'
          )}
        </p>
      </div>
    );
  };

  const renderCalendar = () => (
    <VenueCalendar
      {...props}
      spaces={spaces}
      venue={venue}
      spaceServiceClash={spaceDisabledForService(service)}
      date={date || formatDate(new Date(), dateFormatForAPI)}
      setDate={updateDate}
      initialValues={{ ...getInitialValues(), date }}
    />
  );

  return (
    <div id="venue-spaces" className="venue-spaces" data-cy="venueSpaces">
      <h2>{`Rooms at ${venue.name}`}</h2>
      <SpaceFilters
        showPropertyFilter={false}
        currentViewType={currentView}
        changeViewType={showCalendarToggle ? changeViewType : undefined}
        aggregates={aggregates}
      >
        <FloorplansButton venue={venue} />
      </SpaceFilters>
      {currentView === 'calendar' ? renderCalendar() : renderList()}
    </div>
  );
};

export default VenueSpaces;
